import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, EnvelopeSimple, LinkedinLogo, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { CONTACT } from "../data/content";
import { Reveal } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Vul uw naam, e-mail en bericht in.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Bedankt! Uw bericht is verzonden.");
      setSent(true);
      setForm(initial);
    } catch (err) {
      toast.error("Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-ballk-light bg-[#FAFAFB] px-4 py-3.5 text-ballk-indigo placeholder:text-ballk-steel/60 focus:outline-none focus:border-ballk-purple focus:bg-white transition-colors";

  return (
    <div data-testid="contact-page">
      <section className="pt-40 pb-12">
        <div className="container-ballk max-w-4xl">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow mb-5">Contact</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-heading font-bold text-ballk-indigo text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Laten we uw project bespreken.
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-6 text-ballk-steel text-lg max-w-2xl leading-relaxed">
            Neem contact op voor een vrijblijvende kennismaking. Wij reageren doorgaans binnen één werkdag.
          </motion.p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-ballk grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-ballk-light rounded-2xl p-8 md:p-10 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
              {sent && (
                <div className="mb-6 flex items-center gap-2 rounded-xl bg-ballk-purple/10 text-ballk-purple px-4 py-3 text-sm font-medium" data-testid="contact-success">
                  <CheckCircle size={20} weight="fill" /> Uw bericht is ontvangen.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-ballk-indigo mb-2">Naam *</label>
                  <input data-testid="contact-name" className={field} value={form.name} onChange={update("name")} placeholder="Uw naam" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ballk-indigo mb-2">E-mail *</label>
                  <input data-testid="contact-email" type="email" className={field} value={form.email} onChange={update("email")} placeholder="naam@bedrijf.nl" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ballk-indigo mb-2">Telefoon</label>
                  <input data-testid="contact-phone" className={field} value={form.phone} onChange={update("phone")} placeholder="06 12 34 56 78" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ballk-indigo mb-2">Onderwerp</label>
                  <input data-testid="contact-subject" className={field} value={form.subject} onChange={update("subject")} placeholder="Waar gaat het over?" />
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-sm font-medium text-ballk-indigo mb-2">Bericht *</label>
                <textarea data-testid="contact-message" rows={5} className={field} value={form.message} onChange={update("message")} placeholder="Vertel ons over uw project..." />
              </div>
              <button data-testid="contact-submit" type="submit" disabled={loading}
                className="mt-7 inline-flex items-center justify-center gap-2 rounded-xl bg-ballk-purple px-7 py-3.5 text-sm font-medium text-white hover:bg-ballk-purple-dark transition-all duration-300 disabled:opacity-60 shadow-[0_10px_30px_-10px_rgba(106,44,160,0.6)]">
                {loading ? "Verzenden..." : <>Verstuur bericht <PaperPlaneTilt size={16} weight="bold" /></>}
              </button>
            </form>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="space-y-4">
              {[
                { icon: MapPin, t: "Adres", v: CONTACT.address, href: CONTACT.maps },
                { icon: Phone, t: "Telefoon", v: CONTACT.phone, href: `tel:${CONTACT.phone}` },
                { icon: EnvelopeSimple, t: "E-mail", v: CONTACT.email, href: `mailto:${CONTACT.email}` },
                { icon: LinkedinLogo, t: "LinkedIn", v: "Volg BALLK Project", href: CONTACT.linkedin },
              ].map((c) => (
                <a key={c.t} href={c.href} target={c.t === "LinkedIn" ? "_blank" : undefined} rel="noreferrer"
                  data-testid={`contact-info-${c.t.toLowerCase()}`}
                  className="flex items-center gap-4 bg-white border border-ballk-light rounded-2xl p-5 hover:border-ballk-purple transition-colors group">
                  <span className="h-12 w-12 rounded-xl bg-ballk-purple/10 flex items-center justify-center text-ballk-purple group-hover:bg-ballk-purple group-hover:text-white transition-colors"><c.icon size={24} weight="duotone" /></span>
                  <span>
                    <span className="block text-xs uppercase tracking-wider text-ballk-steel">{c.t}</span>
                    <span className="block font-heading font-semibold text-ballk-indigo">{c.v}</span>
                  </span>
                </a>
              ))}
              <div className="overflow-hidden rounded-2xl border border-ballk-light h-64">
                <iframe title="BALLK Project locatie" src={CONTACT.maps} data-testid="contact-map"
                  className="w-full h-full" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
